import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Loader2 } from 'lucide-react';

import { useAuth } from '../lib/auth/AuthProvider';
import { t } from '../lib/i18n';

/**
 * Banner persistente de impersonación de soporte. Se muestra fijo arriba de
 * TODA la app mientras un super admin ve la plataforma "como" una empresa.
 * Tono `--sun`/`--warn` para que sea imposible de confundir con la sesión real.
 *
 * "Volver al panel admin" corta la impersonación (rehidrata al super admin
 * desde su cookie de refresh, intacta) y navega a /admin.
 */
export function ImpersonationBanner() {
  const { isImpersonating, company, stopImpersonation } = useAuth();
  const navigate = useNavigate();
  const [leaving, setLeaving] = useState(false);

  if (!isImpersonating) return null;

  const back = async () => {
    setLeaving(true);
    const ok = await stopImpersonation();
    setLeaving(false);
    // Si volvió OK → panel admin; si no, el AuthProvider ya cerró sesión y el
    // guard mandará a /login.
    navigate(ok ? '/admin' : '/login', { replace: true });
  };

  return (
    <div className="sticky top-0 z-[60] border-b border-sun/60 bg-sun text-ink shadow-sm">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-2 px-4 py-2">
        <p className="flex items-center gap-2 text-sm font-medium">
          <Eye size={16} className="shrink-0" />
          <span>{t('companyAdmin.impersonate.banner', { company: company?.name ?? '' })}</span>
        </p>
        <button
          type="button"
          onClick={() => void back()}
          disabled={leaving}
          className="inline-flex h-8 items-center gap-2 rounded-token border border-ink/30 bg-ink/5 px-3 text-sm font-semibold text-ink transition-colors hover:bg-ink/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink disabled:opacity-60"
        >
          {leaving && <Loader2 size={14} className="animate-spin" />}
          {t('companyAdmin.impersonate.back')}
        </button>
      </div>
    </div>
  );
}
